/**
 * redshift-setup.ts — Pure frontend SQL generator for Amazon Redshift setup scripts.
 *
 * Generates SQL to create a Lookout-managed service user with a password and
 * database-scoped grants (read-only or read-write). All identifier validation
 * happens at this boundary — callers pass raw strings. The password is entered
 * by the admin and embedded into a single-quoted SQL literal.
 */
import { isSafeSqlIdentifier } from '@server/redshift';

/** Validate a Redshift identifier (preserves case — Redshift folds unquoted names). Throws on invalid input. */
function assertIdent(name: string, label: string): string {
  const trimmed = name.trim();
  if (!isSafeSqlIdentifier(trimmed)) {
    throw new Error(`${label}: must start with a letter or underscore; only letters, numbers, underscores allowed`);
  }
  return trimmed;
}

/** Escape a single-quoted SQL string literal by doubling embedded quotes. */
function quoteLiteral(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/**
 * Reverse ETL's internal schema on Redshift (and Postgres, and Databricks), per rudder-sources'
 * `GetRudderSchema()`. Lower-case: Redshift folds unquoted identifiers down.
 */
const RETL_INTERNAL_SCHEMA = '_rudderstack';

/**
 * Grants on Reverse ETL's internal schema, shared by the password and IAM scripts (ACT2-737).
 *
 * Unconditional, and independent of the read-write flag: that flag is about the customer's own
 * data, while this is Reverse ETL's private bookkeeping schema (`sync_data_*`, `snapshot_*`,
 * `failed_keys_*`, `rej_*`, `sync_log`). Without it an account authenticates, lists tables and
 * saves audiences, then fails at the first activation.
 *
 * `CREATE SCHEMA IF NOT EXISTS` matters as much as the grant: rudder-sources requires the schema to
 * already exist and never creates it. `USAGE, CREATE` is the load-bearing privilege, because rETL
 * owns every table it creates there; the DML line covers tables some other principal created.
 */
export function generateRedshiftRetlSchemaSQL(username: string, database: string): string {
  return `-- Run this step while connected to ${database}. A schema belongs to one database and
-- CREATE SCHEMA cannot name a different one, so running it on the wrong connection creates
-- ${RETL_INTERNAL_SCHEMA} where Reverse ETL will not look for it.
CREATE SCHEMA IF NOT EXISTS ${RETL_INTERNAL_SCHEMA};
GRANT USAGE, CREATE ON SCHEMA ${RETL_INTERNAL_SCHEMA} TO ${username};
GRANT SELECT, INSERT, UPDATE, DELETE
ON ALL TABLES IN SCHEMA ${RETL_INTERNAL_SCHEMA}
TO ${username};`;
}

export interface RedshiftSetupSQLParams {
  username: string;
  password: string;
  database: string;
  /** Read-write grants (INSERT/UPDATE/DELETE) in addition to read-only SELECT. */
  readWrite?: boolean;
}

/**
 * Generate Amazon Redshift setup SQL:
 * - CREATE USER with a password literal
 * - Database-scoped USAGE on all schemas
 * - SELECT on all current and future tables/views (read-only)
 * - INSERT/UPDATE/DELETE on all current and future tables (read-write only)
 *
 * Uses Redshift's database-level grant syntax (`FOR SCHEMAS/TABLES IN DATABASE`),
 * so grants cover every schema without enumerating them.
 */
export function generateRedshiftSetupSQL(params: RedshiftSetupSQLParams): string {
  const { password, readWrite = false } = params;

  const username = assertIdent(params.username, 'Username');
  const database = assertIdent(params.database, 'Database');

  if (!password) {
    throw new Error('Password is required');
  }

  const mode = readWrite ? 'Read-Write' : 'Read-Only';

  const writeStep = readWrite
    ? `
-- Step 4: Grant write access to all current and future tables
GRANT INSERT, UPDATE, DELETE
FOR TABLES IN DATABASE ${database}
TO ${username};
`
    : '';

  return `-- ============================================================
-- Rudder Lookout -- Amazon Redshift ${mode} Setup
-- Requires superuser privileges. Run in the Redshift Query Editor.
-- ============================================================

-- Step 1: Create user
CREATE USER ${username} PASSWORD ${quoteLiteral(password)};

-- Step 2: Grant access to the database
GRANT USAGE
FOR SCHEMAS IN DATABASE ${database}
TO ${username};

-- Step 3: Grant read access to all current and future tables and views
GRANT SELECT
FOR TABLES IN DATABASE ${database}
TO ${username};
${writeStep}
-- Step ${readWrite ? '5' : '4'}: Reverse ETL internal schema (required for activation)
-- Granted even for a read-only account: this is Reverse ETL's own bookkeeping schema,
-- not your data. Skip it and the account explores fine, then fails at first activation.
${generateRedshiftRetlSchemaSQL(username, database)}

-- Step ${readWrite ? '6' : '5'}: Verify (run after setup)
SELECT has_schema_privilege(${quoteLiteral(username.toLowerCase())}, '${RETL_INTERNAL_SCHEMA}', 'CREATE')
  AS can_write_retl_schema;
-- Must be true. Lookout's Test Connection checks the same thing and warns if it is not.
-- The name is lower-cased because CREATE USER ${username} above is unquoted, and Redshift
-- folds unquoted identifiers down -- so the stored user name is ${username.toLowerCase()}.
-- Passing the typed spelling as a string literal would name a user that does not exist, and
-- has_schema_privilege errors rather than returning false.
`;
}
